import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import { BRAND } from "@/config/brand";

interface LegalLink {
  label: string;
  to: string;
}

interface LegalSection {
  title: string;
  paragraphs?: string[];
  list?: string[];
}

interface LegalDoc {
  eyebrow: string;
  current: string;
  title: string;
  description: string;
  sections: LegalSection[];
  related: LegalLink[];
}

/**
 * Date affichée sur les pages légales : calculée au chargement, elle correspond
 * à la version courante des documents présentée sur le site.
 */
const LAST_UPDATE = new Date().toLocaleDateString("fr-FR", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const LEGAL_DOCS: Record<string, LegalDoc> = {
  "mentions-legales": {
    eyebrow: "Légal",
    current: "Mentions légales",
    title: "Mentions légales",
    description: `Informations relatives à l'éditeur et à l'exploitation de la plateforme ${BRAND.name}.`,
    sections: [
      {
        title: "Éditeur de la plateforme",
        paragraphs: [
          `${BRAND.name} est une plateforme logicielle de gestion immobilière (logiciel en ligne) destinée aux propriétaires bailleurs et aux agences immobilières au ${BRAND.country}.`,
          `Nom commercial : ${BRAND.name}. Site web : https://burkina-immo.com. Pays d'exploitation : ${BRAND.country}. Contact : ${BRAND.email} — Téléphone : ${BRAND.phone}.`,
          "Les informations d'identification légale de l'éditeur (raison sociale officielle, RCCM, IFU, adresse juridique, capital social et représentant légal) seront complétées sur cette page lorsque les formalités administratives correspondantes seront finalisées.",
        ],
      },
      {
        title: "Objet de la plateforme",
        paragraphs: [
          `${BRAND.name} est un outil logiciel de gestion immobilière. Il met à disposition des fonctionnalités de création de compte, de gestion des propriétaires, des agences et de leurs employés, ainsi que des immeubles, biens, locataires, contrats, loyers, paiements, impayés, dépenses, maintenances, documents et abonnements.`,
          `${BRAND.name} n'exerce pas d'activité d'agence immobilière, n'intervient pas en qualité de propriétaire ni de gestionnaire immobilier mandaté, et n'est ni un organisme financier ni un service public.`,
        ],
      },
      {
        title: "Responsable de la publication",
        paragraphs: [
          `Le responsable de la publication est joignable à l'adresse de contact indiquée ci-dessus : ${BRAND.email}.`,
          "Ses informations nominatives seront précisées lorsque les formalités administratives correspondantes seront finalisées.",
        ],
      },
      {
        title: "Hébergement",
        paragraphs: [
          "Les coordonnées de l'hébergeur de la plateforme seront complétées et publiées sur cette page lorsque ces informations seront finalisées. D'ici là, toute demande relative à l'hébergement peut être adressée à l'adresse de contact ci-dessus.",
        ],
      },
      {
        title: "Propriété intellectuelle",
        paragraphs: [
          `Le site ${BRAND.name}, son identité visuelle, ses interfaces, ses textes, ses éléments graphiques et les logiciels qui le composent sont protégés par les droits applicables.`,
          "Toute reproduction, représentation, adaptation ou exploitation, totale ou partielle, par quelque moyen que ce soit, sans autorisation écrite préalable, est interdite.",
          "Les informations saisies par un utilisateur dans son espace (immeubles, biens, locataires, contrats, loyers, paiements, documents, etc.) restent la propriété de cet utilisateur et relèvent de sa responsabilité.",
        ],
      },
      {
        title: "Responsabilité",
        paragraphs: [
          `${BRAND.name} est un outil logiciel de gestion immobilière. La plateforme fournit des outils de saisie, de suivi et de gestion ; elle ne se substitue pas aux obligations légales, comptables, fiscales ou administratives de l'utilisateur, ni aux vérifications et décisions qui relèvent de sa responsabilité.`,
          "L'utilisateur demeure responsable de l'exactitude des informations qu'il saisit et des décisions qu'il prend sur la base des éléments affichés dans son espace.",
          "L'éditeur met en œuvre des moyens raisonnables pour assurer le bon fonctionnement de la plateforme, sans pouvoir garantir une absence totale d'interruption ou d'erreur.",
        ],
      },
    ],
    related: [
      { label: "Politique de confidentialité", to: "/confidentialite" },
      { label: "Conditions générales d'utilisation", to: "/cgu" },
    ],
  },

  confidentialite: {
    eyebrow: "Légal",
    current: "Confidentialité",
    title: "Politique de confidentialité",
    description: `Comment ${BRAND.name} collecte, utilise et protège les données saisies sur la plateforme.`,
    sections: [
      {
        title: "Données concernées",
        paragraphs: [
          `${BRAND.name} traite les données nécessaires au fonctionnement du service de gestion immobilière. Selon les fonctionnalités réellement utilisées, ces données peuvent comprendre :`,
        ],
        list: [
          "nom, prénom ou nom complet, adresse e-mail et numéro de téléphone",
          "informations de compte et données de connexion",
          "informations liées au rôle de l'utilisateur (propriétaire, agence, employé d'agence, administrateur de la plateforme)",
          "informations relatives à l'agence, à ses employés et à leurs autorisations",
          "informations relatives aux propriétaires",
          "informations relatives aux immeubles et aux biens",
          "informations relatives aux locataires",
          "informations relatives aux contrats",
          "informations relatives aux loyers, aux paiements et aux impayés",
          "informations relatives aux dépenses",
          "informations relatives aux maintenances et interventions",
          "informations relatives aux abonnements et aux paiements d'abonnement",
          "documents téléversés, lorsque l'utilisateur utilise cette fonctionnalité",
          "informations techniques nécessaires au fonctionnement et à la sécurité du service",
        ],
      },
      {
        title: "Finalités du traitement",
        paragraphs: ["Ces données sont utilisées pour les finalités suivantes :"],
        list: [
          "création et gestion du compte utilisateur",
          "authentification et gestion des accès",
          "gestion immobilière (immeubles et biens)",
          "gestion des propriétaires, des agences et de leurs employés",
          "gestion des locataires et des contrats",
          "suivi des loyers, des paiements et des impayés",
          "gestion des dépenses et des maintenances",
          "gestion et conservation des documents",
          "gestion des abonnements et de leur suivi",
          "sécurité du service et prévention des accès non autorisés",
          "fonctionnement, maintenance et amélioration du service",
        ],
      },
      {
        title: "Données relatives à l'activité immobilière",
        paragraphs: [
          "Les utilisateurs peuvent enregistrer dans leur espace des informations relatives à leur activité immobilière : immeubles, biens, locataires, contrats, loyers, paiements, dépenses, maintenances et documents.",
          `${BRAND.name} n'est pas propriétaire de ces données et n'en devient pas titulaire du fait de leur hébergement. L'utilisateur reste responsable des informations qu'il saisit dans son compte, dans le cadre applicable.`,
        ],
      },
      {
        title: "Sécurité",
        paragraphs: [
          "La plateforme met en œuvre les mécanismes de protection suivants, effectivement présents dans le service :",
        ],
        list: [
          "authentification des utilisateurs",
          "contrôle d'accès selon le rôle (propriétaire, agence, employé d'agence, administrateur)",
          "isolation des données par compte et par périmètre",
          "politiques de sécurité appliquées au niveau de la base de données (Row Level Security)",
          "stockage privé des documents déposés par les utilisateurs",
          "génération d'URLs signées et temporaires pour la consultation des documents",
          "mesures de protection contre les accès non autorisés",
        ],
      },
      {
        title: "Conservation",
        paragraphs: [
          "Les données sont conservées pendant la durée nécessaire au fonctionnement du service et au respect des obligations applicables.",
          "La fin ou l'expiration d'un abonnement n'entraîne pas à elle seule la suppression immédiate des données contenues dans un espace utilisateur. La suppression du compte est réalisée selon les mécanismes prévus par le service et les obligations applicables.",
        ],
      },
      {
        title: "Droits des utilisateurs",
        paragraphs: [
          `Vous pouvez demander l'accès à vos données, leur rectification ou leur suppression, ainsi que formuler une demande relative à leur traitement, en écrivant à ${BRAND.email}.`,
          "Les demandes sont traitées dans les conditions prévues par les règles applicables. Cette page présente la pratique actuelle du service et ne constitue pas une procédure juridique spécifique.",
        ],
      },
      {
        title: "Cookies et stockage local",
        paragraphs: [
          "Le détail des cookies et stockages utilisés par la plateforme est présenté sur la page dédiée.",
        ],
      },
    ],
    related: [
      { label: "Politique de cookies", to: "/cookies" },
      { label: "Mentions légales", to: "/mentions-legales" },
      { label: "Conditions générales d'utilisation", to: "/cgu" },
    ],
  },

  cgu: {
    eyebrow: "Légal",
    current: "CGU",
    title: "Conditions générales d'utilisation",
    description: `Les règles encadrant l'utilisation de la plateforme ${BRAND.name} par ses utilisateurs.`,
    sections: [
      {
        title: "Objet",
        paragraphs: [
          `${BRAND.name} est une solution logicielle qui permet aux utilisateurs de gérer leurs activités immobilières : propriétaires, agences, employés d'agence, immeubles, biens, locataires, contrats, loyers, paiements, impayés, dépenses, maintenances, documents et abonnements.`,
          "Les présentes conditions définissent les modalités d'accès et d'utilisation de la plateforme.",
        ],
      },
      {
        title: "Utilisateurs concernés",
        paragraphs: ["La plateforme s'adresse aux catégories d'utilisateurs suivantes :"],
        list: [
          "propriétaire : gestion de son propre patrimoine",
          "agence : gestion d'un portefeuille de propriétaires, d'immeubles et de biens",
          "employé d'agence : utilisation de la plateforme pour le compte d'une agence, selon les autorisations qui lui sont accordées",
          "administrateur de la plateforme : gestion et supervision du service",
        ],
      },
      {
        title: "Compte utilisateur",
        paragraphs: ["La création et l'utilisation d'un compte supposent le respect des règles suivantes :"],
        list: [
          "l'utilisateur fournit des informations exactes, complètes et à jour lors de la création de son compte",
          "les identifiants de connexion sont personnels et confidentiels",
          "l'utilisateur est responsable de l'usage fait depuis son compte et doit informer l'éditeur en cas d'utilisation non autorisée",
          "l'utilisateur ne doit pas partager son compte en dehors de son périmètre autorisé",
          "l'accès, la suspension ou la désactivation d'un compte peuvent intervenir conformément aux règles du service, notamment en cas de manquement aux présentes conditions",
        ],
      },
      {
        title: "Utilisation du service",
        paragraphs: ["L'utilisateur s'engage à utiliser la plateforme dans un cadre licite et loyal. Sont notamment interdits :"],
        list: [
          "toute utilisation frauduleuse du service",
          "toute tentative de contournement des contrôles d'accès ou des autorisations",
          "tout accès ou toute tentative d'accès aux données d'un autre compte",
          "tout usage abusif du service, y compris la perturbation volontaire de son fonctionnement",
          "l'introduction volontaire de contenu malveillant dans la plateforme",
        ],
      },
      {
        title: "Données saisies par l'utilisateur",
        paragraphs: [
          "L'utilisateur est responsable des données qu'il saisit dans son compte : informations relatives aux propriétaires, immeubles, biens, locataires, contrats, loyers, paiements, dépenses, maintenances et documents.",
          "Il lui appartient de disposer des droits et bases nécessaires pour saisir ces informations et de respecter les obligations qui lui sont applicables dans le cadre de son activité.",
        ],
      },
      {
        title: "Disponibilité du service",
        paragraphs: [
          "L'éditeur met en œuvre des moyens raisonnables pour assurer la disponibilité et le bon fonctionnement de la plateforme.",
          "Aucun taux de disponibilité chiffré n'est garanti. Le service peut être interrompu temporairement pour des opérations de maintenance, de mise à jour ou pour des raisons techniques indépendantes de la volonté de l'éditeur.",
        ],
      },
      {
        title: "Évolution du service",
        paragraphs: [
          "Le service peut évoluer : ses fonctionnalités peuvent être corrigées, modifiées, enrichies ou réorganisées afin d'améliorer la plateforme ou de l'adapter aux besoins des utilisateurs.",
        ],
      },
      {
        title: "Suspension et résiliation",
        paragraphs: [
          "L'utilisateur peut changer de formule ou mettre fin à son abonnement depuis son espace Abonnement, selon les mécanismes prévus par le service.",
          "L'éditeur peut suspendre ou désactiver un compte en cas de manquement aux présentes conditions, ou conformément aux règles du service. Ces dispositions sont complétées, pour les abonnements payants, par les conditions générales de vente.",
        ],
      },
    ],
    related: [
      { label: "Politique de confidentialité", to: "/confidentialite" },
      { label: "Conditions générales de vente", to: "/cgv" },
      { label: "Mentions légales", to: "/mentions-legales" },
    ],
  },

  cgv: {
    eyebrow: "Légal",
    current: "CGV",
    title: "Conditions générales de vente",
    description: `Les conditions applicables aux formules d'abonnement de la plateforme ${BRAND.name}.`,
    sections: [
      {
        title: "Objet",
        paragraphs: [
          "Les présentes conditions générales de vente régissent la souscription aux formules d'abonnement de la plateforme.",
          "Elles complètent les conditions générales d'utilisation de la plateforme.",
        ],
      },
      {
        title: "Formules et tarifs",
        paragraphs: [
          "Les formules d'abonnement et leurs prix en FCFA sont les suivants :",
        ],
        list: [
          "Gratuit : 0 FCFA par mois / 0 FCFA par an",
          "Essentiel : 10 000 FCFA par mois / 108 000 FCFA par an",
          "Professionnel : 15 000 FCFA par mois / 162 000 FCFA par an",
          "Agence : 20 000 FCFA par mois / 216 000 FCFA par an",
        ],
      },
      {
        title: "Limites des formules",
        paragraphs: [
          "Chaque formule comporte des limites portant notamment sur le nombre d'immeubles, de biens, de locataires, de contrats et d'utilisateurs. Ces limites sont celles affichées sur la page Tarifs et dans votre espace Abonnement. Elles peuvent être atteintes sans que la formule soit automatiquement modifiée.",
        ],
      },
      {
        title: "Facturation mensuelle ou annuelle",
        paragraphs: [
          "L'abonnement peut être souscrit selon une périodicité mensuelle ou annuelle. La facturation annuelle correspond à une remise de 10 % par rapport au cumul de douze paiements mensuels pour les formules payantes.",
          "La formule Gratuite ne comporte pas de période ni de renouvellement.",
        ],
      },
      {
        title: "Abonnement et renouvellement",
        paragraphs: [
          "La souscription précise la formule choisie, la périodicité retenue, la date de début et la date de fin de la période en cours.",
          "À la fin de la période, l'abonnement peut être renouvelé depuis votre espace Abonnement, au mensuel ou à l'annuel. L'utilisateur peut également changer de formule depuis cet espace.",
        ],
      },
      {
        title: "Paiement",
        paragraphs: [
          "Le paiement de l'abonnement donne accès aux fonctionnalités prévues par la formule choisie.",
          "Le règlement s'effectue selon les modalités affichées dans votre espace Abonnement au moment de la souscription ou du renouvellement. Aucun prestataire de paiement tiers n'est désigné sur cette page.",
        ],
      },
      {
        title: "Fin ou expiration de l'abonnement",
        paragraphs: [
          "La fin ou l'expiration d'un abonnement n'entraîne pas la suppression automatique des données de votre espace.",
          "Des restrictions d'accès métier peuvent s'appliquer conformément au fonctionnement du service : la consultation et l'exportation des données restent accessibles, tandis que la création et la modification sont bloquées jusqu'au renouvellement de l'abonnement.",
        ],
      },
      {
        title: "Remboursement",
        paragraphs: [
          "Aucune politique de remboursement spécifique n'est définie à ce jour.",
          "Les conditions applicables sont communiquées au moment du paiement et conformément aux règles en vigueur.",
        ],
      },
      {
        title: "Contact",
        paragraphs: [
          `Pour toute question relative à un abonnement, vous pouvez écrire à ${BRAND.email}.`,
        ],
      },
    ],
    related: [
      { label: "Conditions générales d'utilisation", to: "/cgu" },
      { label: "Politique de confidentialité", to: "/confidentialite" },
      { label: "Abonnement", to: "/abonnement" },
    ],
  },

  cookies: {
    eyebrow: "Légal",
    current: "Cookies",
    title: "Politique de cookies",
    description: `Cookies et stockages utilisés par la plateforme ${BRAND.name}.`,
    sections: [
      {
        title: "Qu'est-ce qu'un cookie ou un stockage local ?",
        paragraphs: [
          "Un cookie est un petit fichier déposé lors de votre navigation. Certaines applications utilisent également un stockage local dans le navigateur pour mémoriser des informations nécessaires à leur fonctionnement.",
        ],
      },
      {
        title: "Cookies et stockages nécessaires au service",
        paragraphs: [
          "La plateforme utilise uniquement les cookies et stockages nécessaires à son fonctionnement et à sa sécurité, notamment :",
        ],
        list: [
          "l'authentification de l'utilisateur et le maintien de sa session",
          "la conservation du jeton de connexion afin d'éviter une reconnexion à chaque page",
          "les préférences nécessaires au fonctionnement du service",
          "les éléments techniques liés à la sécurité du service",
        ],
      },
      {
        title: "Technologies de mesure d'audience",
        paragraphs: [
          "À ce jour, la plateforme n'utilise aucun outil de mesure d'audience ni aucune technologie de suivi publicitaire. Si de tels outils étaient ajoutés, cette page serait mise à jour en conséquence.",
        ],
      },
      {
        title: "Gestion des cookies",
        paragraphs: [
          "Vous pouvez configurer votre navigateur pour refuser ou supprimer les cookies et les données de stockage local.",
          "Le refus ou la suppression de ces éléments peut empêcher le maintien de votre session et rendre certaines fonctionnalités inaccessibles.",
          "La plateforme ne met pas en place de bandeau de consentement avancé, les cookies et stockages utilisés étant nécessaires à son fonctionnement.",
        ],
      },
      {
        title: "Contact",
        paragraphs: [
          `Pour toute question relative aux cookies et aux stockages utilisés, vous pouvez écrire à ${BRAND.email}.`,
        ],
      },
    ],
    related: [
      { label: "Politique de confidentialité", to: "/confidentialite" },
      { label: "Mentions légales", to: "/mentions-legales" },
    ],
  },
};

interface LegalPageProps {
  slug: string;
}

export default function LegalPage({ slug }: LegalPageProps) {
  const doc = LEGAL_DOCS[slug];

  if (!doc) {
    return (
      <div className="container-page py-20">
        <h1 className="text-2xl font-heading font-semibold text-foreground-950">Document introuvable</h1>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <PageHero eyebrow={doc.eyebrow} current={doc.current} title={doc.title} description={doc.description} />

      <section className="py-14 md:py-20 bg-background-50">
        <div className="container-page max-w-3xl">
          <div className="flex flex-col gap-8">
            {doc.sections.map((section, index) => (
              <Reveal as="article" key={section.title} delay={Math.min(index, 6) * 60}>
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-950">{section.title}</h2>
                {section.paragraphs && section.paragraphs.length > 0 && (
                  <div className="mt-3 flex flex-col gap-3">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph} className="text-sm leading-relaxed text-foreground-700">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                )}
                {section.list && section.list.length > 0 && (
                  <ul className="mt-3 flex flex-col gap-2">
                    {section.list.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
                        <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Reveal>
            ))}
          </div>

          <nav aria-label="Documents légaux" className="mt-12 rounded-lg border border-background-200 bg-background-100/60 p-5">
            <h2 className="text-sm font-heading font-semibold text-foreground-950">Voir aussi</h2>
            <ul className="mt-3 flex flex-wrap gap-2">
              {doc.related.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="inline-flex items-center gap-1.5 rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-label font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-file-text-line text-sm" aria-hidden="true" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <p className="mt-6 rounded-lg border border-background-200 bg-background-100/60 p-5 text-sm leading-relaxed text-foreground-600">
            Dernière mise à jour : {LAST_UPDATE}. Certaines informations administratives seront complétées sur ces
            pages lorsque les formalités correspondantes seront finalisées. Pour toute question, écrivez à {BRAND.email}.
          </p>
        </div>
      </section>
    </div>
  );
}